import { Marker } from "../display/Marker.js";
import { VOAttachment, VOMarkerLayout, VOMarkerList } from "../model/DataStructure.js";
import { Translation } from "../model/Language.js";
import { BaseElement3d, BaseElement3dOptions } from "./BaseElement3d.js";
import { HeightMap } from "./HeightMap.js";

export interface Globe3dOptions extends BaseElement3dOptions {
    radius: number;
    assetsUrl: string;
    markerLists: VOMarkerList[];
    heightMap?: HeightMap;
}

interface Globe3dPoiOptions {
    image?: VOAttachment;
    label: Translation;
    lat: number;
    lng: number;
    offset?: number;
    layout?: VOMarkerLayout;
}

interface Globe3dBillboardOptions {
    label: Translation;
    lat: number;
    lng: number;
}

export class Globe3d extends BaseElement3d {


    public onRequestPosition = e5.Signal.create<[{ lat: number; lng: number; zoom?: number }]>();

    private _radius: number;
    private _assetsUrl: string;
    private _heightMap: HeightMap;

    constructor(options: Globe3dOptions) {
        super();

        options = e5.object.merge({

        } as Globe3dOptions, options);

        this._heightMap = options.heightMap;

        this.create(options);

        this._radius = options.radius;
        this._assetsUrl = options.assetsUrl;

        this.addCubeGlobe();

        // this.addBillboards();
        // this.addPois();

        for (var markerList of options.markerLists) {
            if (!markerList.visible) {
                continue;
            }

            for (var marker of markerList.markers) {
                if (!marker.visible) {
                    continue;
                }

                if (markerList.style === "tooltip") {
                    this.addPoi({
                        label: {
                            de: marker.label_de,
                            en: marker.label_en
                        },
                        lat: marker.lat,
                        lng: marker.lng,
                        image: marker.image,
                        layout: marker.layout
                    });
                } else if (markerList.style === "simple") {
                    this.addBillboard({
                        label: {
                            de: marker.label_de,
                            en: marker.label_en
                        },
                        lat: marker.lat,
                        lng: marker.lng
                    });
                }
            }
        }
    }

    private addBillboard(options: Globe3dBillboardOptions): void {
        var m = new Marker(this._canvas, {
            lat: options.lat,
            lng: options.lng,
            minZoom: 8
        });
        var div = document.createElement("div");
        div.className = "awi-globe3d__continent-marker";
        m.element.appendChild(div);
        lang.set(div, options.label);
        this._canvas.addMarker(m);
    }

    private addPoi(options: Globe3dPoiOptions): void {
        var minZoom = 17;

        var layout = options.layout;
        if (!layout) {
            layout = "right";
        }

        var offset = options.offset === undefined ? 10 : options.offset;

        var offsetX = 0;
        var offsetY = 0;
        var originX: string = "50%";
        var originY: string = "50%";

        if (layout === "left") {
            originX = "100%";
            originY = "50%";
            offsetX = -offset;
        }
        else if (layout === "right") {
            originX = "0%";
            originY = "50%";
            offsetX = offset;
        }
        else if (layout === "bottom") {
            originX = "50%";
            originY = "0";
            offsetY = offset;
        }
        else if (layout === "bottom-right") {
            originX = "20px";
            originY = "0";
            offsetY = offset;
        }
        else if (layout === "bottom-left") {
            originX = "calc(100% - 20px)";
            originY = "0";
            offsetY = offset;
        }
        else if (layout === "top") {
            originX = "50%";
            originY = "100%";
            offsetY = -offset;
        }
        else if (layout === "top-right") {
            originX = "20px";
            originY = "100%";
            offsetY = -offset;
        }
        else if (layout === "top-left") {
            originX = "calc(100% - 20px)";
            originY = "100%";
            offsetY = -offset;
        }

        var m = new Marker(this._canvas, {
            lat: options.lat,
            lng: options.lng,
            minZoom: minZoom,
            originX: originX,
            originY: originY,
            offsetX: offsetX,
            offsetY: offsetY,
            zIndexOffset: 48
        });

        var div = document.createElement("div");
        div.className = "awi-globe3d__poi-marker awi-globe3d__poi-marker--" + layout;
        m.element.appendChild(div);

        var tri = document.createElement("span");
        tri.className = "awi-globe3d__poi-marker-triangle";
        div.appendChild(tri);

        var label = document.createElement("div");
        label.className = "awi-globe3d__poi-marker-label";
        lang.setType(label, "html", options.label);
        div.appendChild(label);

        if (options.image) {
            var img = document.createElement("div");
            img.className = "awi-globe3d__poi-marker-img";
            img.style.backgroundImage = `url(${options.image.url})`;

            if (layout === "left"
                || layout === "bottom-left"
                || layout === "top-left") {
                div.prepend(img);
            } else {
                div.appendChild(img);
            }
        }

        this._canvas.addMarker(m);

        var plus = new Marker(this._canvas, {
            lat: options.lat,
            lng: options.lng,
            maxZoom: minZoom,
            originX: "50%",
            originY: "50%",
            interactive: true
            // minZoom: 4
        });

        var d = document.createElement("div");
        d.className = "awi-globe3d__plus";
        d.innerHTML = `<span class="awi-globe3d__plus-icon fal fa-plus"></div>`;
        var click = new e5.Click(d);
        click.onClick.add(() => {
            this.onRequestPosition.dispatch({
                lat: options.lat,
                lng: options.lng,
                zoom: minZoom + 0.1
            });
        });
        plus.element.appendChild(d);
        this._canvas.addMarker(plus);
    }

    public async addCubeGlobe(): Promise<any> {

        const loader = new THREE.TextureLoader();

        var base = "";
        var ext = "";

        // base = "textures/deprecated/cubemap_highres/";
        // ext = "png";

        // base = "textures/deprecated/blue/";
        // ext = "jpg";

        // base = "textures/deprecated/light/";
        // ext = "png";

        // base = "textures/deprecated/striped/";
        // ext = "png";

        // base = "textures/deprecated/nasa/";
        // ext = "png";

        // base = "textures/deprecated/solar/";
        // ext = "png";

        // base = "textures/deprecated/earth-1024x1024_100q/";
        // ext = "jpg";
        // base = "textures/deprecated/earth-1024x1024_80q/";
        // ext = "jpg";

        base = "textures/earth-2048x2048_80q/";
        ext = "jpg";

        const sides: string[] = [
            "px",
            "nx",
            "py",
            "ny",
            "pz",
            "nz"
        ];

        const cubeMaterials = [];
        if (this._heightMap) {
            const light = new THREE.AmbientLight(0xFFFFFF, 0.3); // soft white light
            this._canvas.scene.add(light);
            const directionalLight = new THREE.DirectionalLight(0xFFFFFF, 0.7);
            this._canvas.onUpdate.add(() => {
                directionalLight.position.copy(this._canvas.camera.position);
                directionalLight.lookAt(new THREE.Vector3());
            });
            directionalLight.rotateY(-180 * Math.PI / 180);
            this._canvas.scene.add(directionalLight);
            
            for (var side of sides) {
                cubeMaterials.push(this.addMaterial(new THREE.MeshStandardMaterial(
                    {
                        map: loader.load(this._assetsUrl + base + side + "." + ext)
                    })
                ));
            }
        } else {
            for (var side of sides) {
                cubeMaterials.push(this.addMaterial(new THREE.MeshBasicMaterial(
                    {
                        map: loader.load(this._assetsUrl + base + side + "." + ext)
                    })
                ));
            }
        }

        var sphereG = new THREE.BoxBufferGeometry(this._radius, this._radius, this._radius, 32, 32, 32);

        var matrix = new THREE.Matrix4();
        matrix.makeRotationY(-(Math.PI / 2));
        sphereG.applyMatrix4(matrix);

        var points = sphereG.attributes.position.array;
        var count = points.length / 3;
        var index = 0;
        var indexB = 0;
        var pos = new THREE.Vector3();
        const res = new Float32Array(count * 3);
        for (var u = 0; u < count; ++u) {
            pos.x = points[index++];
            pos.y = points[index++];
            pos.z = points[index++];

            pos.normalize();

            var radius = this._radius;

            if (this._heightMap) {
                var latLng = this._canvas.vectorToLatLng(pos);

                radius += this._heightMap.getHeight(latLng.y, latLng.x) * 5;
            }


            pos.multiplyScalar(radius);

            res[indexB++] = pos.x;
            res[indexB++] = pos.y;
            res[indexB++] = pos.z;
        }
        sphereG.setAttribute('position', new THREE.BufferAttribute(res, 3));
        sphereG.computeVertexNormals();

        var sphere = new THREE.Mesh(sphereG, cubeMaterials);
        this.element.add(sphere);
    }
}